import ExcelJS from 'exceljs';
import i18n from '../../../i18n';
import { BreakdownInfo } from '../../../pages/Home/types';
import { buildExportDownloadFileName } from '../exportFileName';
import { compareExportNamesDigitLetterPinyin, sortCastItemsByNO } from '../../../utils/elementSortUtils';
import { SlotType } from '../../../types/schedule.types';
import { writeWorkbookBufferWithProgress } from '../excelWriteProgress';
import {
  SlotDataCache,
  ElementDataCache,
  ElementRelationDataCache
} from '../../../types/cache.types';
import { ElementInfo, ElementType } from '../../../types/element.types';

export type SupportedLanguage = 'zh-CN' | 'zh-HK' | 'en';

const t = (key: string, opts?: Record<string, unknown> & { lng?: string }) => i18n.t(key, opts);

const COLORS = {
  GRAY3: '#757575',
  BLACK: '#000000',
  BORDER: '#000000'
};

const ALL_ELEMENT_TYPES: ElementType[] = [
  ElementType.CAST,
  ElementType.FEATURED,
  ElementType.BACKGROUND,
  ElementType.STUNT_PLAYERS,
  ElementType.ANIMALS,
  ElementType.VEHICLES,
  ElementType.ART_PROPS,
  ElementType.WARDROBE,
  ElementType.HAIR_MAKEUP,
  ElementType.SFX_MU,
  ElementType.SFX,
  ElementType.VFX,
  ElementType.CAMERA,
  ElementType.LIGHTING,
  ElementType.SOUND,
  ElementType.ADDL_LABOR,
  ElementType.SPECIAL_EQUIPMENT,
  ElementType.MISC,
  ElementType.NOTE
];

const MAX_QTY_TYPES = new Set<ElementType>([
  ElementType.FEATURED,
  ElementType.BACKGROUND,
  ElementType.STUNT_PLAYERS,
  ElementType.ANIMALS,
  ElementType.VEHICLES
]);

export const EXCEL_ELEMENT_SHOOTING_DATES_SUMMARY_MARGINS_INCH = {
  left: 0.35,
  right: 0.35,
  top: 0.35,
  bottom: 0.5,
  header: 0.3,
  footer: 0.3
} as const;

const hexToArgb = (hex: string): string => `FF${hex.replace('#', '').toUpperCase()}`;

const setCellBorder = (cell: ExcelJS.Cell) => {
  const borderColor = { argb: hexToArgb(COLORS.BORDER) };
  cell.border = {
    top: { style: 'thin', color: borderColor },
    left: { style: 'thin', color: borderColor },
    bottom: { style: 'thin', color: borderColor },
    right: { style: 'thin', color: borderColor }
  };
};

function applyPrintLayout(worksheet: ExcelJS.Worksheet, language: SupportedLanguage) {
  worksheet.pageSetup = {
    ...worksheet.pageSetup,
    orientation: 'landscape',
    horizontalCentered: true,
    margins: { ...EXCEL_ELEMENT_SHOOTING_DATES_SUMMARY_MARGINS_INCH }
  };
  const footerBody = t('export.excelPageFooter', { lng: language });
  worksheet.headerFooter = {
    ...worksheet.headerFooter,
    oddFooter: `&C&8${footerBody}`
  };
}

const normalizeDate = (raw?: string): string | null => {
  if (!raw) return null;
  const d = raw.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
};

const MAX_EXCEL_CELL_TEXT_LENGTH = 32767;

const sanitizeExcelText = (raw: string): string => {
  const cleaned = Array.from(raw)
    .filter((ch) => {
      const code = ch.charCodeAt(0);
      const isControl =
        (code >= 0 && code <= 8) ||
        code === 11 ||
        code === 12 ||
        (code >= 14 && code <= 31) ||
        code === 127;
      return !isControl;
    })
    .join('');
  if (cleaned.length <= MAX_EXCEL_CELL_TEXT_LENGTH) return cleaned;
  return cleaned.slice(0, MAX_EXCEL_CELL_TEXT_LENGTH);
};

const toSafeSheetName = (raw: string, used: Set<string>): string => {
  let base = sanitizeExcelText(raw || 'Sheet')
    .replace(/[\\/?*:[\]]/g, '_')
    .replace(/^'+|'+$/g, '')
    .trim()
    .slice(0, 31) || 'Sheet';
  if (!used.has(base)) {
    used.add(base);
    return base;
  }
  let i = 2;
  while (i < 100) {
    const suffix = `_${i}`;
    const candidate = `${base.slice(0, 31 - suffix.length)}${suffix}`;
    if (!used.has(candidate)) {
      used.add(candidate);
      return candidate;
    }
    i += 1;
  }
  return base;
};

type ItemRow = {
  no: string;
  name: string;
  ext: string | number;
  dayCount: number;
  dayList: string;
};

export const exportElementShootingDatesSummaryExcel = async (
  breakdownInfo: BreakdownInfo,
  language: SupportedLanguage = 'zh-CN',
  _secondaryLanguage?: SupportedLanguage,
  breakdownId?: string,
  effectiveScheduleId?: string | null,
  getSlotCache?: (scheduleId: string) => SlotDataCache | null,
  getElementCache?: (breakdownId: string) => ElementDataCache | null,
  getElementRelationCache?: (breakdownId: string) => ElementRelationDataCache | null,
  onProgress?: (progress: number) => void
): Promise<void> => {
  onProgress?.(10);
  if (!breakdownId || !effectiveScheduleId || !getSlotCache || !getElementCache || !getElementRelationCache) {
    throw new Error(t('export.exportNeedScheduleId', { lng: language }) as string);
  }

  const slotCache = getSlotCache(effectiveScheduleId);
  const slots = slotCache?.slots || [];
  const elements = getElementCache(breakdownId)?.elements || [];
  const elementRelations = getElementRelationCache(breakdownId)?.elementRelations || [];

  const sceneDatesMap = new Map<string, Set<string>>();
  slots.forEach((pt) => {
    if (!(pt.slotType === SlotType.SHOOT || !pt.slotType)) return;
    if (!pt.slotBelongSceneID) return;
    const d = normalizeDate(pt.slotDate || '');
    if (!d) return;
    const set = sceneDatesMap.get(pt.slotBelongSceneID) || new Set<string>();
    set.add(d);
    sceneDatesMap.set(pt.slotBelongSceneID, set);
  });

  const sceneIdsByItem = new Map<string, Set<string>>();
  const maxQtyByItem = new Map<string, number>();
  elementRelations.forEach((rel) => {
    const sceneSet = sceneIdsByItem.get(rel.elementID) || new Set<string>();
    sceneSet.add(rel.sceneID);
    sceneIdsByItem.set(rel.elementID, sceneSet);
    const qty = Math.max(1, rel.elementQtyInScene || 1);
    const prev = maxQtyByItem.get(rel.elementID) || 0;
    if (qty > prev) maxQtyByItem.set(rel.elementID, qty);
  });

  const itemsByType = new Map<ElementType, ElementInfo[]>();
  elements.forEach((item) => {
    if (!item.elementType) return;
    const list = itemsByType.get(item.elementType) || [];
    list.push(item);
    itemsByType.set(item.elementType, list);
  });
  onProgress?.(35);

  const workbook = new ExcelJS.Workbook();
  const usedSheetNames = new Set<string>();
  const filmName = typeof window !== 'undefined' ? localStorage.getItem('selectedFilmName') || '' : '';
  const scheduleName = slotCache?.scheduleInfo?.scheduleName || '';
  const scheduleVersionLabel = t('export.castPdfScheduleVersionLabel', { lng: language }) as string;
  const breakdownVersionLabel = t('export.castPdfBreakdownVersionLabel', { lng: language }) as string;
  const scriptVersionLabel = t('export.scriptVersion', { lng: language }) as string;
  const labelSeparator = t('common.labelSeparator', { lng: language }) as string;
  const pipeSeparator = t('common.pipeSeparator', { lng: language }) as string;
  const title = sanitizeExcelText(
    `《${filmName}》${scheduleVersionLabel}${labelSeparator}${scheduleName}${pipeSeparator}${breakdownVersionLabel}${labelSeparator}${breakdownInfo.breakdownName || ''}${pipeSeparator}${scriptVersionLabel}${labelSeparator}${breakdownInfo.breakdownScriptOverview || ''}`
  );

  ALL_ELEMENT_TYPES.forEach((type, idx) => {
    const typeLabel = sanitizeExcelText(t(`elementType.${type}`, { lng: language }) as string);
    const sheet = workbook.addWorksheet(toSafeSheetName(typeLabel, usedSheetNames));
    sheet.columns = [{ width: 5 }, { width: 28 }, { width: 8 }, { width: 8 }, { width: 71 }];
    sheet.addRow([title]);
    const titleCell = sheet.getCell(1, 1);
    titleCell.font = { size: 8, bold: false, color: { argb: hexToArgb(COLORS.BLACK) } };
    titleCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: false };

    const thirdHeader = type === ElementType.CAST
      ? (t('export.abbr', { lng: language }) as string)
      : (MAX_QTY_TYPES.has(type) ? (t('export.maxQuantity', { lng: language }) as string) : '');
    const header = sheet.addRow([
      '#',
      typeLabel,
      thirdHeader,
      t('export.shootDaysCount', { lng: language }),
      t('export.shootDates', { lng: language })
    ]);
    header.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: hexToArgb(COLORS.GRAY3) } };
      cell.font = { color: { argb: hexToArgb(COLORS.BLACK) }, size: 8, bold: false };
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      setCellBorder(cell);
    });

    const currentItems = [...(itemsByType.get(type) || [])];
    if (type === ElementType.CAST) {
      const castSorted = sortCastItemsByNO(currentItems);
      currentItems.length = 0;
      currentItems.push(...castSorted);
    } else {
      currentItems.sort((a, b) => compareExportNamesDigitLetterPinyin(
        (a.elementName || a.elementAbbr || '').trim(),
        (b.elementName || b.elementAbbr || '').trim()
      ));
    }

    const rows: ItemRow[] = currentItems.map((item) => {
      const relatedSceneIds = sceneIdsByItem.get(item.elementID) || new Set<string>();
      const dateSet = new Set<string>();
      relatedSceneIds.forEach((sceneId) => {
        const dates = sceneDatesMap.get(sceneId);
        dates?.forEach((d) => dateSet.add(d));
      });
      const dates = Array.from(dateSet).sort();
      return {
        no: type === ElementType.CAST ? (item.elementNO || '') : '',
        name: sanitizeExcelText((item.elementName || item.elementAbbr || '').trim()),
        ext:
          type === ElementType.CAST
            ? sanitizeExcelText(item.elementAbbr || '')
            : (MAX_QTY_TYPES.has(type)
              ? (item.elementMaxQtyInBreakdown ?? maxQtyByItem.get(item.elementID) ?? '')
              : ''),
        dayCount: dates.length,
        dayList: sanitizeExcelText(dates.join(' / '))
      };
    });

    rows.forEach((rowData) => {
      const row = sheet.addRow([rowData.no, rowData.name, rowData.ext, rowData.dayCount, rowData.dayList]);
      row.eachCell((cell) => {
        cell.font = { size: 8, bold: false, color: { argb: hexToArgb(COLORS.BLACK) } };
        cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: false };
        setCellBorder(cell);
      });
      row.getCell(5).alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
    });

    applyPrintLayout(sheet, language);
    const progress = 35 + Math.round(((idx + 1) / ALL_ELEMENT_TYPES.length) * 40);
    onProgress?.(Math.min(75, progress));
  });

  const fileName = buildExportDownloadFileName({
    lng: language,
    filmName,
    optionDisplayName: t('export.elementsShootingDatesSummaryTable', { lng: language }) as string,
    optionFallbackI18nKey: 'export.elementsShootingDatesSummaryTable',
    breakdownDisplayName: scheduleName,
    breakdownFallbackI18nKey: 'export.schedule',
    extension: 'xlsx'
  });

  const buffer = await writeWorkbookBufferWithProgress(workbook, onProgress, { start: 80, end: 98 });
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  onProgress?.(100);
};
